import React, { useState } from 'react';
import { CheckboxRow, Divider, ErrorText, Link, Modal, PillButton, Row, Stack, Text } from 'library/ui';
import { useCenterContact } from 'parent-portal/shared/useCenterContact';

const CLEARINGHOUSE_LINK = 'https://crw.flclearinghouse.com/';

const AGREEMENTS = [
    {
        key: 'verify',
        label: 'I will verify each substitute’s eligibility in the Clearinghouse before booking them.',
        subtitle:
            'Before booking a substitute, I will look them up in the Florida Clearinghouse and confirm that they are Eligible for DCF Child Care and that their screening is current. I will accurately report their eligibility status to Tandem.',
    },
    {
        key: 'roster',
        label: 'I will add each substitute to my center’s Clearinghouse roster.',
        subtitle:
            'Before a substitute begins working at my center, I will add them to my facility’s Clearinghouse roster as required, unless my licensing representative indicates otherwise.',
    },
    {
        key: 'monitoring',
        label: 'I will notify Tandem if a substitute’s eligibility changes.',
        subtitle:
            'If I receive notice through continuous monitoring that a substitute is no longer eligible, I will notify Tandem promptly and will not allow them to continue working through Tandem.',
    },
];

export function FloridaClearinghousePolicyModal({ onAgree }: { onAgree: () => Promise<void> }) {
    const { email, phoneNumber } = useCenterContact();
    const [checked, setChecked] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(false);

    const allChecked = AGREEMENTS.every((agreement) => checked.includes(agreement.key));

    function toggle(key: string, isChecked: boolean) {
        setChecked((previous) => (isChecked ? [...previous, key] : previous.filter((item) => item !== key)));
    }

    function agree() {
        setSubmitting(true);
        setError(false);
        onAgree()
            .catch(() => setError(true))
            .finally(() => setSubmitting(false));
    }

    return (
        <Modal
            id="florida-clearinghouse-policy-modal"
            title="Florida Substitute Screening Responsibilities"
            subtitle="Please review and agree before booking substitutes."
            onClose={() => undefined}
            dismissible={false}
            width={620}
        >
            <Stack gap="md">
                <Text size="sm" component="p" lineHeight="relaxed" tone="secondary">
                    Based on guidance from the Florida Department of Early Learning (DEL) and Department of Children and
                    Families (DCF), your center is responsible for verifying each substitute&rsquo;s background
                    screening eligibility in the{' '}
                    <Link href={CLEARINGHOUSE_LINK} size="sm">
                        DCF Clearinghouse
                    </Link>{' '}
                    before they can be booked.
                </Text>
                <Text size="sm" component="p" lineHeight="relaxed" tone="secondary">
                    Please confirm that you understand and agree to the following responsibilities:
                </Text>
                <Stack gap="sm">
                    {AGREEMENTS.map((agreement) => (
                        <CheckboxRow
                            key={agreement.key}
                            checked={checked.includes(agreement.key)}
                            onChange={(isChecked) => toggle(agreement.key, isChecked)}
                            label={agreement.label}
                            subtitle={agreement.subtitle}
                        />
                    ))}
                </Stack>
                <Divider />
                <Text size="sm" component="p" lineHeight="relaxed" tone="secondary">
                    Questions? Contact {email} or call {phoneNumber}.
                </Text>
                {error && <ErrorText>We could not save your agreement. Please try again.</ErrorText>}
                <Row justify="end" fullWidth>
                    <PillButton
                        id="florida-clearinghouse-policy-agree-button"
                        disabled={!allChecked || submitting}
                        onClick={agree}
                    >
                        {submitting ? 'Saving...' : 'I Agree'}
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}
